import React from 'react';
import { toast } from 'react-toastify';
import ActionButton from '../../../Components/Common/ActionButton';
import ChangeRequestList from '../../../Components/crude/ChangeRequestList';
import useChangeRequests from '../../../Components/crude/useChangeRequests';
import {
  CHANGE_REQUEST_ENDPOINTS,
  CHANGE_REQUEST_STATUS,
} from '../../../Components/crude/changeRequestConstants';
import { crudeApi } from '../../../helpers/crude_api_helper';

// My Changes — the session user's own change requests.
// The server filters by UserId and applies NO status filter, so every status
// (Awaiting Review / Awaiting Approve / Approved / Rejected / Canceled) shows.
//
// Cancel is only valid while the request is still AwaitingReview; the server
// re-validates this (a wrong status returns 422 with the reason in `detail`),
// so the status check below is for UX only, not security. Ownership IS
// enforced server-side (403 if the caller isn't the request's creator) — moot
// here since GetMyList is already scoped to the logged-in user, but relevant
// if Cancel is ever exposed outside this page.
const MyChangesView = () => {
  const {
    items,
    totalRows,
    loading,
    perPage,
    searchInput,
    setSearchInput,
    setPage,
    setPerPage,
    handleSort,
    refetch,
  } = useChangeRequests({
    endpoint: CHANGE_REQUEST_ENDPOINTS.MY_LIST,
    detailKey: 'detailMyList',
  });

  const handleCancel = async (row, notes) => {
    try {
      await crudeApi.postForm(CHANGE_REQUEST_ENDPOINTS.CANCEL, {
        requestId: row.id,
        notes,
      });
      toast.success('Change request canceled');
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      refetch();
    }
  };

  const renderActions = (row, { confirm }) =>
    row.status === CHANGE_REQUEST_STATUS.AWAITING_REVIEW ? (
      <ActionButton
        variant='destructive'
        size='sm'
        confirm={false}
        icon='ri-close-line'
        onClick={() =>
          confirm({
            title: 'Cancel Change Request',
            message: `Cancel your change on "${row.dataKey}"? Its status will become Canceled and it will be reverted in the feeder module.`,
            confirmLabel: 'Yes, Cancel',
            confirmColor: 'danger',
            showNotes: true,
            onConfirm: notes => handleCancel(row, notes),
          })
        }
      >
        Cancel
      </ActionButton>
    ) : (
      <span className='text-muted'>-</span>
    );

  return (
    <ChangeRequestList
      title='My Changes'
      cardTitle='My Changes'
      documentTitle='My Changes'
      data={items}
      totalRows={totalRows}
      loading={loading}
      perPage={perPage}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      onChangePage={setPage}
      onChangeRowsPerPage={newPerPage => {
        setPerPage(newPerPage);
        setPage(1);
      }}
      onSort={handleSort}
      showStatus
      renderActions={renderActions}
      emptyText='You have no changes'
    />
  );
};

export default MyChangesView;
